import React, { useState, useEffect, useRef } from "react";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";

/**
 * Performance changes vs previous version:
 *
 * 1. web3forms script loaded lazily here via useEffect — only runs when
 *    the user actually visits /contact, not on every page of the app.
 *
 * 2. useForm is now used properly — register, handleSubmit, formState
 *    are wired to the form so react-hook-form actually validates fields.
 *    Previously only setValue was used (the library was imported but unused).
 *
 * 3. hCaptcha ref added so the widget can be reset after successful submit.
 *
 * 4. Result state replaced with { type, message } for proper success/error
 *    styling instead of string comparison (result === "Sending...").
 *
 * 5. Variants consolidated — subheadingVariants merged into headingVariants
 *    with a delay difference; fewer named objects, same visual result.
 */

// ─── Variants ─────────────────────────────────────────────────────────────────

const cardVariants = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const formVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.35 } },
};

const fieldVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

// ─── Component ────────────────────────────────────────────────────────────────

const Contact = () => {
  const [status, setStatus] = useState({ type: "idle", message: "" });
  const captchaRef = useRef(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm();

  // Lazy-load web3forms only when Contact mounts
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://web3forms.com/client/script.js";
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const onHCaptchaChange = (token) => {
    setValue("h-captcha-response", token, { shouldValidate: true });
  };

  const onSubmit = async (formValues) => {
    setStatus({ type: "loading", message: "Sending..." });

    const formData = new FormData();
    formData.append("access_key", "62ad52e8-52b1-4697-bb47-62050f08dac9");
    Object.entries(formValues).forEach(([k, v]) => formData.append(k, v));

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();

      if (data.success) {
        setStatus({ type: "success", message: "Message sent — thank you!" });
        reset();
        captchaRef.current?.resetCaptcha();
      } else {
        setStatus({ type: "error", message: "Failed to send. Please try again." });
      }
    } catch {
      setStatus({ type: "error", message: "Something went wrong. Please try again." });
    }
  };

  const inputClass =
    "block w-full px-4 py-3 border bg-white/70 placeholder-stone-400 text-stone-900 rounded-md focus:outline-none focus:ring-2 focus:ring-[#BC3F4A]/40 focus:border-[#BC3F4A] transition";

  const borderClass = (hasError) =>
    hasError ? "border-red-400" : "border-[#8a4b32]";

  return (
    <>
      <Helmet>
        <title>Contact Sanguine Accent — Fine Art Photography & String Art Greeting Cards</title>
        <meta
          name="description"
          content="Connect with Sanguine Accent for inquiries about fine art prints, string art greeting cards, collaborations, or general questions."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://sanguineaccent.com/contact" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Contact Sanguine Accent",
            url: "https://sanguineaccent.com/contact",
          })}
        </script>
      </Helmet>

      {/* Breadcrumb */}
      <div className="py-6 md:pt-44 pt-28 px-4 max-w-5xl mx-auto">
        <nav aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-2">
            <li>
              <Link
                to="/"
                className="font-sourcesans text-sm font-medium text-stone-700 hover:text-[#BC3F4A] transition"
              >
                Home
              </Link>
            </li>
            <li className="flex items-center">
              <svg
                className="w-3 h-3 text-stone-400 mx-2"
                fill="none"
                viewBox="0 0 6 10"
                aria-hidden="true"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 9 4-4-4-4"
                />
              </svg>
              <span className="font-sourcesans text-sm font-medium text-stone-500">
                Connect
              </span>
            </li>
          </ol>
        </nav>
      </div>

      {/* Contact Form */}
      <div className="py-12 flex items-center justify-center bg-[#f5f3ea] px-4">
        <motion.div
          className="max-w-md w-full space-y-6 bg-white/70 backdrop-blur-sm rounded-md p-6 shadow-md will-change-transform"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={cardVariants}
        >
          <div className="text-center space-y-1">
            <motion.h2
              className="text-2xl md:text-3xl font-medium text-stone-800 leading-snug"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              Get in Touch
            </motion.h2>

            <motion.p
              className="mt-4 text-base md:text-lg text-stone-700 leading-relaxed"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.25 }}
            >
              Questions about our cards? Interested in supporting our mission?
              Just want to say hello? We'd love to connect.
            </motion.p>
          </div>

          <motion.form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4"
            variants={formVariants}
            noValidate
          >
            {/* Name */}
            <motion.div variants={fieldVariants}>
              <input
                {...register("name", { required: "Name is required" })}
                type="text"
                placeholder="Your Name"
                autoComplete="name"
                className={`${inputClass} ${borderClass(errors.name)}`}
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>
              )}
            </motion.div>

            {/* Email */}
            <motion.div variants={fieldVariants}>
              <input
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Enter a valid email address",
                  },
                })}
                type="email"
                placeholder="Your Email"
                autoComplete="email"
                className={`${inputClass} ${borderClass(errors.email)}`}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>
              )}
            </motion.div>

            {/* Message */}
            <motion.div variants={fieldVariants}>
              <textarea
                {...register("message", { required: "Message is required" })}
                rows={4}
                placeholder="Your Message"
                className={`${inputClass} ${borderClass(errors.message)}`}
              />
              {errors.message && (
                <p className="mt-1 text-xs text-red-500">{errors.message.message}</p>
              )}
            </motion.div>

            {/* hCaptcha */}
            <motion.div variants={fieldVariants}>
              <HCaptcha
                ref={captchaRef}
                sitekey="50b2fe65-b00b-4b9e-ad62-3ba471098be2"
                reCaptchaCompat={false}
                onVerify={onHCaptchaChange}
              />
            </motion.div>

            {/* Submit */}
            <motion.button
              variants={fieldVariants}
              type="submit"
              disabled={isSubmitting || status.type === "loading"}
              className="w-full py-3 px-4 rounded-md text-white bg-[#BC3F4A] hover:bg-[#a3323b] transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status.type === "loading" ? "Sending…" : "Send a Message"}
            </motion.button>

            {/* Status message */}
            {status.message && (
              <p
                aria-live="polite"
                role="status"
                className={`text-center text-sm font-sourcesans ${
                  status.type === "success" ? "text-green-700" : "text-red-600"
                }`}
              >
                {status.message}
              </p>
            )}
          </motion.form>
        </motion.div>
      </div>
    </>
  );
};

export default Contact;